import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAirports } from '../services/airportService';

const PAGE_LIMIT = 15;

const SearchableAirportSelect = ({
  value = '',
  onChange,
  placeholder = 'Select Airport',
  required = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [airports, setAirports] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState(null);

  const containerRef = useRef(null);
  const dropdownListRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastExecutedSearchRef = useRef('');

  // Fetch airports logic
  const fetchAirportsData = useCallback(async (pageNum = 1, searchQuery = '', append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const params = {
        page: pageNum,
        limit: PAGE_LIMIT,
        status: 1, // Only active airports
        ...(searchQuery?.trim() && { search: searchQuery.trim() })
      };

      const res = await getAirports(params);
      const items = res?.data || [];
      const meta = res?.meta || {};

      setTotalPages(meta.totalPages || (items.length >= PAGE_LIMIT ? pageNum + 1 : pageNum));
      setPage(pageNum);
      lastExecutedSearchRef.current = searchQuery;

      if (append) {
        setAirports(prev => {
          const existingIds = new Set(prev.map(a => a.id));
          const uniqueNew = items.filter(a => !existingIds.has(a.id));
          return [...prev, ...uniqueNew];
        });
      } else {
        setAirports(items);
        if (dropdownListRef.current) {
          dropdownListRef.current.scrollTop = 0;
        }
      }
    } catch (error) {
      console.error('Error fetching airports:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Sync external value with display text & selected airport
  useEffect(() => {
    if (!value) {
      setSelectedAirport(null);
      if (!isOpen) setInputValue('');
      return;
    }

    // Try finding the airport from the current airports list
    const found = airports.find(a => a.code === value);
    if (found) {
      setSelectedAirport(found);
      if (!isOpen) setInputValue(`${found.code} — ${found.name}`);
    } else if (!selectedAirport || selectedAirport.code !== value) {
      // If not yet in loaded list, show code or placeholder
      if (!isOpen) setInputValue(value);
    }
  }, [value, airports, isOpen, selectedAirport]);

  // Initial load when opened for the first time
  useEffect(() => {
    if (isOpen && airports.length === 0) {
      fetchAirportsData(1, '', false);
    }
  }, [isOpen, airports.length, fetchAirportsData]);

  // Trigger search after typing stops (debounce 500ms)
  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputValue(text);

    if (!isOpen) setIsOpen(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (text !== lastExecutedSearchRef.current) {
        fetchAirportsData(1, text, false);
      }
    }, 500);
  };

  // Immediate search on blur or Enter key
  const triggerImmediateSearch = (query) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (query !== lastExecutedSearchRef.current) {
      fetchAirportsData(1, query, false);
    }
  };

  const handleInputBlur = (e) => {
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) {
      return;
    }
    triggerImmediateSearch(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      triggerImmediateSearch(inputValue);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Handle scroll for infinite scrolling
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight <= 35) {
      if (!loading && !loadingMore && page < totalPages) {
        fetchAirportsData(page + 1, lastExecutedSearchRef.current, true);
      }
    }
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        // If user left without selecting, reset text to selected airport if any
        if (selectedAirport) {
          setInputValue(`${selectedAirport.code} — ${selectedAirport.name}`);
        } else if (value) {
          setInputValue(value);
        } else {
          setInputValue('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [selectedAirport, value]);

  const handleSelect = (airport) => {
    setSelectedAirport(airport);
    setInputValue(`${airport.code} — ${airport.name}`);
    setIsOpen(false);
    if (onChange) {
      onChange(airport.code, airport);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedAirport(null);
    setInputValue('');
    lastExecutedSearchRef.current = '';
    if (onChange) {
      onChange('', null);
    }
    fetchAirportsData(1, '', false);
  };

  return (
    <div className="searchable-select" ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <div
        className="ss-input-wrap"
        style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}
        onClick={() => {
          if (!disabled) setIsOpen(prev => !prev);
        }}
      >
        <input
          type="text"
          className="form-input"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
          disabled={disabled}
          required={required && !value}
          style={{
            width: '100%',
            paddingRight: value ? 56 : 36,
            cursor: 'pointer'
          }}
        />

        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            title="Clear selection"
            style={{
              position: 'absolute',
              right: 32,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              color: '#94A3B8',
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            &times;
          </button>
        )}

        <svg
          className={`ss-arrow ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--grey)"
          strokeWidth="2"
          style={{
            position: 'absolute',
            right: 14,
            top: '50%',
            transform: `translateY(-50%) ${isOpen ? 'rotate(180deg)' : ''}`,
            transition: 'transform 0.2s',
            pointerEvents: 'none'
          }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      {isOpen && (
        <div
          ref={dropdownListRef}
          className="ss-dropdown"
          onScroll={handleScroll}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #E2E8F0',
            borderRadius: 12,
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: 250,
            overflowY: 'auto'
          }}
        >
          {loading ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--grey)', fontSize: 13 }}>
              Loading airports...
            </div>
          ) : airports.length > 0 ? (
            <>
              {airports.map(ap => {
                const isSelected = value === ap.code;
                return (
                  <div
                    key={ap.id}
                    className={`ss-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(ap)}
                    style={{
                      padding: '11px 16px',
                      fontSize: 13,
                      color: isSelected ? 'var(--blue)' : 'var(--navy)',
                      background: isSelected ? '#F0F7FF' : 'transparent',
                      fontWeight: isSelected ? 600 : 400,
                      cursor: 'pointer',
                      borderBottom: '1px solid #F8FAFC'
                    }}
                  >
                    <span><strong>{ap.code}</strong> — {ap.name}</span>
                    {isSelected && (
                      <span style={{ color: 'var(--blue)', fontSize: 14, fontWeight: 700 }}>✓</span>
                    )}
                  </div>
                );
              })}

              {loadingMore && (
                <div style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--grey)', fontSize: 12, background: '#FAFCFE' }}>
                  Loading more airports...
                </div>
              )}
            </>
          ) : (
            <div className="ss-no-results" style={{ padding: '20px', fontSize: 13, color: 'var(--grey)', textAlign: 'center' }}>
              No airports found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableAirportSelect;
